"use client";

import { useEffect, useMemo } from "react";
import { isServer, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { CommandBarProvider } from "@/hooks/use-command-bar";
import { CommandBar } from "@/components/command-bar";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EntityInstanceModalHost } from "@/components/entity-instance-table/entity-instance-modal-host";
import { UnsavedChangesProvider } from "@/components/navigation/unsaved-changes-guard";
import { makeQueryClient } from "@/lib/query-client";
import { clearPersistedQueries, createSessionPersister } from "@/lib/query-persister";
import { ENTITY_DATA_SELECTION } from "@/services/entity-fragments";

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

interface ProvidersProps {
  children: React.ReactNode;
  customerId: string | null;
  userId: string | null;
}

export const Providers = ({ children, customerId, userId }: ProvidersProps) => {
  const queryClient = getQueryClient();
  const persister = useMemo(
    () => (customerId && userId ? createSessionPersister(customerId, userId) : null),
    [customerId, userId],
  );

  // No session means logged out or expired, so nothing of the last user may stay behind.
  useEffect(() => {
    if (!persister) void clearPersistedQueries();
  }, [persister]);

  const content = (
    <NuqsAdapter>
      <TooltipProvider>
        <CommandBarProvider>
          <UnsavedChangesProvider>
            {children}
            <CommandBar />
            <EntityInstanceModalHost />
          </UnsavedChangesProvider>
        </CommandBarProvider>
      </TooltipProvider>
    </NuqsAdapter>
  );

  if (!persister) {
    return <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>;
  }

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        // A changed selection must not restore lists of the old shape.
        buster: ENTITY_DATA_SELECTION,
        maxAge: 7 * 24 * 60 * 60 * 1000,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) =>
            query.queryKey[0] === "entities" && query.state.status === "success",
        },
      }}
    >
      {content}
    </PersistQueryClientProvider>
  );
};
